import React from "react";
import { ClassResponse } from "../../services/api.response.service";
import { Link } from "react-router-dom";
import { RouteEndpoints } from "../../services/route.service";

export interface BundleCourseItem {
  id?: number;
  name: string;
  icon: string;
}

export interface CourseBundleData {
  id?: number | string;
  link_name?: string;
  name: string;
  providerName: string;
  originalPrice: number;
  discountedPrice: number;
  currency: string;
  courses: BundleCourseItem[];
  /** First class logo when bundle has no image */
  imageUrl?: string;
  /** Same as ClassCard bundle: pay site /class-bundle/{id} */
  bundlePayUrl?: string;
  /** When false, show only bundle monthly price (no strikethrough) */
  showComparePrice?: boolean;
}

interface CourseBundleCardProps {
  classDetails: ClassResponse;
  bundle?: CourseBundleData | null;
}

const DEFAULT_BUNDLE_COURSES: BundleCourseItem[] = [
  { name: "Physics for SHS", icon: "las la-bolt" },
  { name: "Biology for SHS", icon: "las la-dna" },
  { name: "Chemistry for SHS", icon: "las la-atom" },
  { name: "Integrated Science for SHS", icon: "las la-link" },
  { name: "Agric Science for SHS", icon: "las la-leaf" },
];

const CourseBundleCard = ({ classDetails, bundle }: CourseBundleCardProps) => {
  const contentProvider = classDetails?.content_provider;
  const providerName = contentProvider?.name ?? "eCampus Education";

  const bundleData: CourseBundleData = bundle ?? {
    name: `The ${classDetails?.category?.name ?? "SHS Science"} Bundle`,
    providerName,
    originalPrice: 15,
    discountedPrice: 10,
    currency: classDetails?.subscription_currency ?? "GHS",
    courses: DEFAULT_BUNDLE_COURSES,
    link_name: classDetails?.link_name,
  };

  const bundleLink =
    bundleData.link_name
      ? RouteEndpoints.CLASS_DETAILS(bundleData.link_name)
      : RouteEndpoints.CLASS_BUNDLES;

  return (
    <div
      className="card ec-card mt-3 p-3"
      style={{
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #E8E8E8",
        backgroundColor: "#F6F6FF",
      }}
    >
      <h5
        style={{
          color: "#E4593C",
          fontWeight: 600,
          fontSize: "18px",
          marginBottom: "6px",
        }}
      >
        Get the bundle!
      </h5>
      <p
        style={{
          fontSize: "12px",
          marginBottom: "16px",
          lineHeight: 1.4,
          color: "#343B53",
        }}
      >
        Subscribe to a package of related courses including this one at a
        discount.
      </p>

      {/* Bundle card display */}
      <div
        style={{
          backgroundColor: "#3F5784",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className="d-flex flex-column align-items-center"
          style={{
            backgroundColor: "#F4F6F9",
            borderRadius: "10px",
            padding: "16px 20px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            minWidth: "200px",
            maxWidth: "100%",
          }}
        >
          {bundleData.imageUrl ? (
            <img
              src={bundleData.imageUrl}
              alt=""
              style={{
                width: "120px",
                height: "120px",
                objectFit: "cover",
                borderRadius: "10px",
                marginBottom: "10px",
              }}
            />
          ) : (
            <i
              className="las la-flask"
              style={{
                fontSize: "28px",
                color: "#5A6B7D",
                marginBottom: "10px",
              }}
            />
          )}
          <div className="d-flex flex-column align-items-center w-100">
            <span
              style={{
                fontWeight: 600,
                fontSize: "15px",
                color: "#1F3040",
                textAlign: "center",
              }}
            >
              {bundleData.name}
            </span>
            <div className="d-flex align-items-center gap-2 mt-1 flex-wrap justify-content-center">
              <span
                style={{
                  fontSize: "12px",
                  color: "#6B7280",
                }}
              >
                {bundleData.providerName}
              </span>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#fff",
                  backgroundColor: "#5B9BD5",
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                Bundle
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div
        className="d-flex flex-row gap-2 mb-3"
        style={{ alignItems: "center" }}
      >
        <span style={{ fontSize: "13.5px", color: "#4A4A4A", fontWeight: 600 }}>
          Price per month
        </span>
        <div className="d-flex align-items-baseline gap-2">
          {bundleData.showComparePrice !== false &&
            bundleData.originalPrice > bundleData.discountedPrice && (
              <span
                style={{
                  fontSize: "19.7px",
                  color: "#BBD9D9",
                  textDecoration: "line-through",
                  fontWeight: 600,
                }}
              >
                {bundleData.currency} {bundleData.originalPrice}
              </span>
            )}
          <span
            style={{
              fontSize: "19.7px",
              fontWeight: 600,
              color: "#0D6364",
            }}
          >
            {bundleData.currency} {bundleData.discountedPrice}
          </span>
        </div>
      </div>

      {/* CTA: bundle pay URL (same as ClassCard on class bundles page) or in-app link */}
      {bundleData.bundlePayUrl && bundleData.bundlePayUrl !== "#" ? (
        <a
          href={bundleData.bundlePayUrl}
          target="_blank"
          rel="noreferrer"
          className="btn w-100 text-decoration-none d-flex justify-content-center align-items-center"
          style={{
            border: "2px solid #DF6438",
            color: "#343B53",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: "100px",
            padding: "10px 16px",
            backgroundColor: "#fff",
          }}
        >
          Learn more {">"}
        </a>
      ) : (
        <Link
          to={bundleLink}
          className="btn w-100 text-decoration-none d-flex justify-content-center align-items-center"
          style={{
            border: "2px solid #DF6438",
            color: "#343B53",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: "100px",
            padding: "10px 16px",
            backgroundColor: "#fff",
          }}
        >
          Learn more {">"}
        </Link>
      )}

      <hr style={{ marginBottom: "12px" }} />

      {/* What you get */}
      <div className="mt-0">
        <h6
          className="header-fw mb-2"
          style={{ fontSize: "14px", color: "#486284" }}
        >
          What you get:
        </h6>
        <div className="d-flex flex-column gap-1">
          {bundleData.courses.map((course, index) => (
            <div
              key={course.id ?? index}
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "13px", color: "#4A4A4A" }}
            >
              <i
                className={course.icon}
                style={{ fontSize: "16px", color: "#6B7280", minWidth: "20px" }}
              />
              <span>{course.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CourseBundleCard;
